import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { Redirect, Route, Switch, useLocation } from "wouter-preact";
import { authReady, dailyLimitReached, session } from "./state";
import { Ask } from "./views/Ask";
import { BetaFull } from "./views/BetaFull";
import { Chat } from "./views/Chat";
import { Login } from "./views/Login";
import { Privacy } from "./views/Privacy";
import { Profile } from "./views/Profile";
import { Shared } from "./views/Shared";
import { Terms } from "./views/Terms";
import { Sidebar } from "./components/Sidebar";
import { DailyLimitDialog } from "./components/DailyLimitDialog";

const SITE_NAME = "FaB AI Judge";

/** Page label shown after the site name in the document title. */
function pageTitle(path: string): string {
  if (path.startsWith("/chat")) return "Chat";
  if (path.startsWith("/ask")) return "Ask";
  if (path.startsWith("/profile")) return "Profile";
  if (path.startsWith("/s/")) return "Shared ruling";
  if (path.startsWith("/terms")) return "Terms of Service";
  if (path.startsWith("/privacy")) return "Privacy Policy";
  if (path.startsWith("/login")) return "Sign in";
  if (path.startsWith("/beta-full")) return "Closed beta";
  return "";
}

function usePageTitle() {
  const [location] = useLocation();
  useEffect(() => {
    const page = pageTitle(location);
    document.title = page ? `${SITE_NAME} ◆ ${page}` : SITE_NAME;
  }, [location]);
}

function RequireAuth({ children }: { children: ComponentChildren }) {
  if (!session.value) return <Redirect to="/login" />;
  return <>{children}</>;
}

/**
 * Shared chrome for signed-in pages: a docked sidebar on wide screens that
 * collapses into an off-canvas drawer on narrow ones.
 */
function AppShell({ children }: { children: ComponentChildren }) {
  return (
    <div class="shell">
      <Sidebar />
      <div class="shell__main">{children}</div>
    </div>
  );
}

export function App() {
  usePageTitle();

  // Wait for the persisted session before routing, so a signed-in reload does
  // not flash the login page.
  if (!authReady.value) {
    return (
      <div class="view">
        <div class="view__center content">
          <p class="ask__lede">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Switch>
        <Route path="/s/:id">
          {(params) => <Shared id={params.id} />}
        </Route>
        <Route path="/login" component={Login} />
        <Route path="/beta-full" component={BetaFull} />
        <Route path="/terms" component={Terms} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/ask">
          <RequireAuth>
            <AppShell>
              <Ask />
            </AppShell>
          </RequireAuth>
        </Route>
        <Route path="/chat">
          <RequireAuth>
            <AppShell>
              <Chat />
            </AppShell>
          </RequireAuth>
        </Route>
        <Route path="/profile">
          <RequireAuth>
            <AppShell>
              <Profile />
            </AppShell>
          </RequireAuth>
        </Route>
        <Route>
          <Redirect to={session.value ? "/ask" : "/login"} />
        </Route>
      </Switch>
      {dailyLimitReached.value && <DailyLimitDialog />}
    </>
  );
}
